



































































import Vue, { PropOptions } from 'vue'
import { mapGetters } from 'vuex'
import Tabs from '../components/Tabs.vue'
import {
  Tab as TabProps,
  ApiData,
  Part,
  PullRequest,
  ReplenishmentView,
  Shipment,
} from '../interfaces'
import { Subtab as SubtabProps } from '../interfaces'
import Popover from '@/components/Popover.vue'
import Button from '@/components/Button.vue'
import BrandLogo from '@/components/BrandLogo.vue'
import IconRefresh from '@/components/icons/IconRefresh.vue'
import { AppHub } from '@/event-hub'
import { logUserActivity } from '@/services/AppInsights'

interface Data {}

interface Methods {
  fetchData(): void
  handleTabClick(tabId: string): void
  handleSubTabClick(tabId: string, subTabId: string): void
  changeAccount(account: string): void
  handlePullInventoryClick(): void
  handleRefreshClick(): void
}

interface Computed {
  accountId: string
  inventoryCount: number
  recentShipmentsCount: number
  refreshTime: Date
  isCSO: boolean
  headerVisible: string
  pullsPendingCount: number
  tabs: TabProps[]
  activeTab: TabProps
  activeSubTabs: any
  parts: ApiData<Part[]>
  pulls: ApiData<PullRequest[]>
  replenishments: ReplenishmentView
  shipments: ApiData<Shipment[]>
}

interface Props {}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: 'Dashboard',
  components: { Tabs, Popover, Button, BrandLogo, IconRefresh },
  created() {
    AppHub.$on('refresh', this.fetchData)
    this.fetchData()
  },
  beforeDestroy() {
    AppHub.$off('refresh', this.fetchData)
  },
  computed: {
    ...mapGetters(['accountList']),
    // account was a mapGetter but vetur errors even though its ok that way
    accountId(): string {
      return this.$store.state.accountId
    },
    inventoryCount(): number {
      return this.$store.getters.getAvailableInventoryCount
    },
    recentShipmentsCount(): number {
      return this.$store.getters.recentShipmentsCount
    },
    refreshTime(): Date {
      return this.$store.state.refreshTime
    },
    isCSO(): boolean {
      return this.$store.state.isCSO
    },
    headerVisible(): string {
      return this.$store.state.dashboard.headerVisible
    },
    pullsPendingCount(): number {
      return this.$store.getters.getPullsPendingCount
    },
    tabs(): TabProps[] {
      return [
        {
          id: 'inventory',
          title: this.$tc('inventory'),
          description: this.$tc('availableToPull'),
          count: this.inventoryCount,
          color: '#007faf',
          alert: false,
          subs: [],
          isLoading: this.parts.isLoading,
          hasError: this.parts.hasError,
        },
        {
          id: 'pulls',
          title: this.$tc('pulls'),
          description: this.$tc('pendingReview'),
          count: this.pullsPendingCount,
          color: '#0090da',
          alert: this.isCSO && this.pullsPendingCount > 0,
          subs: [
            {
              id: 'pending',
              title: this.$tc('pending'),
            },
            {
              id: 'history',
              title: this.$tc('history'),
            },
          ],
          isLoading: this.pulls.isLoading,
          hasError: this.pulls.hasError,
        },
        {
          id: 'shipments',
          title: this.$tc('shipments'),
          description: this.$tc('inLastSevenDays'),
          count: this.recentShipmentsCount,
          color: '#00a3e1',
          alert: false,
          subs: [],
          isLoading: this.shipments.isLoading,
          hasError: this.shipments.hasError,
        },
        {
          id: 'replenishments',
          title: this.$tc('replenishments'),
          description: this.$tc('scheduledThisWeek'),
          count: this.replenishments.replenishmentsThisWeek || 0,
          color: '#71c5e8',
          alert: false,
          subs: [],
          isLoading: this.replenishments.isLoading,
          hasError: this.replenishments.hasError,
        },
      ]
    },
    activeTab(): TabProps {
      const match = (this as any).$route.path.match(/^\/[^/]+\/([^/]+)/)
      return match ? this.tabs.find(tab => tab.id === match[1]) || this.tabs[0] : this.tabs[0]
    },
    activeSubTabs() {
      return this.$store.state.currentSubtabs
    },
    parts(): ApiData<Part[]> {
      return this.$store.state.parts
    },
    pulls(): ApiData<PullRequest[]> {
      return this.$store.state.pullRequests
    },
    replenishments(): ReplenishmentView {
      return this.$store.state.replenishmentView
    },
    shipments(): ApiData<Shipment[]> {
      return this.$store.state.shipments
    },
  },
  methods: {
    fetchData() {
      this.$store.dispatch('fetchAccountList')
      this.$store.dispatch('fetchParts')
      this.$store.dispatch('fetchPullRequests')
      this.$store.dispatch('fetchShipments')
      this.$store.dispatch('fetchReplenishments')
    },
    handleTabClick(tabId: string) {
      this.$router.push(`/${this.accountId}/${tabId}`)
    },
    handleSubTabClick(tabId: string, subTabId: string) {
      const subtabState = { parent: tabId, subtab: subTabId }
      this.$store.commit('setCurrentSubtab', subtabState)
      this.$router.push(`/${this.accountId}/${tabId}`)
    },
    changeAccount(accountId: string) {
      this.$store
        .dispatch('setAccountId', accountId)
        .then(() => this.$router.push(`/${accountId}`))
        .catch(() => this.$router.push('/choose-account'))
    },
    handlePullInventoryClick() {
      logUserActivity('PullInventory')
      this.$router.push(`/${this.accountId}/pull-inventory`)
    },
    handleRefreshClick() {
      this.$store.dispatch('refresh')
    },
  },
})
